import { useCallback, useContext, useMemo } from 'react'
import { browserHistory } from '../../services/history'
import { context } from '../context'
import { setServices } from '../actions'
import { useSelectors } from './selectors/use-selectors'
import { usePromoCodesApi } from './use-promo-codes-api'

// Map ?service=<slug> values to the price-list `name` Rails sends down.
// Keep in sync with SERVICE_SLUGS in pages/services/services-plans/index.js.
const SLUG_TO_SERVICE_NAME = {
  exterior_wash_and_wax: 'Exterior Wash & Wax',
  mini_valet: 'Mini Valet',
  mini_valet_plus: 'Mini Valet PLUS',
  dog_owners_valet: 'Dog Owners Valet',
  full_valet: 'Full Valet',
  interior_only_valet: 'Interior Only Valet'
}

export const useSearchParams = () => {
  const { dispatch } = useContext(context)
  const { isUserSignedIn, prices, services } = useSelectors()
  const { apiPromoCodeInfo, apiApplyPromoCode } = usePromoCodesApi()
  const search = useMemo(() => new URLSearchParams(browserHistory.location.search), [])

  const applyPromoCodeParam = useCallback(() => {
    const promoCode = search.get('promo_code')

    if (!promoCode) return

    if (isUserSignedIn) apiApplyPromoCode(promoCode, 'link')
    else apiPromoCodeInfo(promoCode, { via: 'link' })
  }, [search, isUserSignedIn, apiPromoCodeInfo, apiApplyPromoCode])

  // Pre-select a base service from a homepage brick click. URL pattern:
  // /booking?service=mini_valet - resolves slug to the service ID via
  // prices.byId, then dispatches setServices so StepService shows it
  // already chosen.
  const applyServiceParam = useCallback(() => {
    const slug = search.get('service')
    if (!slug) return

    const targetName = SLUG_TO_SERVICE_NAME[slug]
    if (!targetName || !prices?.byId) return

    const baseService = Object.values(prices.byId).find(s => s.name === targetName)
    if (!baseService) return

    const selectedServices = {
      ...services,
      ...(baseService.excludes || []).reduce((acc, id) => ({ ...acc, [id]: false }), {}),
      [baseService.id]: true
    }

    const servicesAvailability = Object.values(prices.byId).reduce((acc, service) => {
      return { ...acc, [service.id]: !(baseService.includes || []).includes(service.id) }
    }, {})

    dispatch(setServices({ services: selectedServices, servicesAvailability }))
  }, [search, dispatch, prices, services])

  return {
    search,
    applyPromoCodeParam,
    applyServiceParam
  }
}
